<template>
  <div class="preview-page-container">
    <div class="floating-buttons-container">
      <div class="floating-btn back-btn" @click="goBack" title="返回">
        <i class="el-icon-arrow-left"></i>
      </div>

      <div 
        v-if="renderedContent.toc" 
        class="floating-btn toc-btn"
        @click="toggleTocSidebar"
        title="目录"
      >
        <i class="el-icon-notebook-2"></i>
      </div>
      
      <div class="floating-btn theme-btn" title="主题">
        <i class="el-icon-postcard"></i>
        <el-select 
          v-model="previewTheme" 
          size="mini" 
          placeholder=""
          class="theme-select-dropdown"
        >
          <el-option
            v-for="theme in previewThemes"
            :key="theme.value"
            :label="theme.label"
            :value="theme.value"
          >
          </el-option>
        </el-select>
      </div>

      <div class="floating-btn export-btn" @click="exportToPdf" title="导出 PDF">
        <i class="el-icon-download"></i>
      </div>

    </div>
    
    <transition name="slide">
      <div 
        v-show="showTocSidebar" 
        class="toc-sidebar"
      >
        <div class="toc-sidebar-header">
          <h3>目录</h3>
          <el-button 
            type="text" 
            icon="el-icon-close" 
            @click="toggleTocSidebar"
            class="toc-close-btn"
          ></el-button>
        </div>
        <div class="toc-sidebar-content" v-html="renderedContent.toc"></div>
      </div>
    </transition>
    
    <div class="preview-wrapper">
      <div class="preview-body">
        <div 
          ref="previewContent"
          id="preview-content" 
          :class="previewThemeClass"
          v-html="renderedContent.html"
          @click="handlePreviewClick"
        ></div>  
      </div>
    </div>
  </div>
</template>

<script>
import '@/css/PreviewTheme.css'; // 主题样式
import {highlightAllCode, setImgStyles} from '@/js/highlight.js';

export default {
  name: 'PreviewPage',
  components: {
  },
  data() {
    return {
      isExporting: false,
      editorContent: '',
      isSyncingScroll: false,
      previewTheme: '0',          // 预览主题，默认为空（默认主题）
      previewThemes: [            // 预览主题选项
        { value: '0', label: '默认主题' },
        { value: '7', label: '缤纷虹' },
        { value: '6', label: '可爱粉' },
        { value: '5', label: '清新蓝' },
        { value: '1', label: '经典白' },
        { value: '2', label: '暗夜黑' },
        { value: '3', label: '护眼绿' },
        { value: '4', label: '简约灰' },
      ],
      // 锚点滚动相关数据
      scrollOffset: 60, // 滚动偏移量
      // 目录侧边栏相关数据
      showTocSidebar: false,
      tocSidebarWidth: 250
    };
  },
  computed: {
    renderedContent(){
        return this.$store.state.renderedContent;
    },
    // 计算预览主题的CSS类名
    previewThemeClass() {
      return `markdown-theme-base theme-${this.previewTheme}`;
    }
  },
  watch: {
    showTocSidebar(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.setupTocClickHandlers();
        });
      }
    }
  },
  mounted() {
    this.$nextTick(()=>{ // 执行代码高亮和图片处理
        setImgStyles();
        highlightAllCode();
    })
  },
  beforeDestroy() {
    // 移除目录点击事件监听器
    const tocContainer = document.querySelector('.toc-sidebar-content');
    if (tocContainer) {
      tocContainer.removeEventListener('click', this.handleTocClick);
    }
  },
  methods: {
    // 返回上一页
    goBack() {
      this.$router.push("/Editor");
    },

    // 导出 PDF 功能
    exportToPdf() {
      // 将预览容器的滚动条置为顶端
      if (this.$refs.previewContent) {
        this.$refs.previewContent.scrollTop = 0;
      }
      window.print();
    },

    // 处理预览区域点击事件
    handlePreviewClick(event) {
      const link = event.target.closest('a[href^="#"]');
      if (link) {
        event.preventDefault();
        const targetId = link.getAttribute('href').substring(1);
        this.scrollToHeading(targetId);
      }
    },

    // 滚动到指定标题
    scrollToHeading(targetId) {
      try {
        const previewContainer = this.$refs.previewContent;
        if (!previewContainer || !targetId) {
          return;
        }
        
        const targetElement = previewContainer.querySelector(`#${targetId}`);
        if (!targetElement) {
          return;
        }
        
        const targetPosition = targetElement.offsetTop - this.scrollOffset;
        
        const maxScroll = Math.max(0, previewContainer.scrollHeight - previewContainer.clientHeight);
        const safePosition = Math.max(0, Math.min(targetPosition, maxScroll));
        
        if (!isFinite(safePosition)) {
          return;
        }
        
        previewContainer.scrollTo({
          top: safePosition,
          behavior: 'auto'
        });
        
        return;
        
      } catch (error) {
        console.error('滚动失败:', error);
        return;
      }
    },
    
    // 切换目录侧边栏
    toggleTocSidebar() {
      this.showTocSidebar = !this.showTocSidebar;
    },
    
    // 为目录链接添加点击事件处理
    setupTocClickHandlers() {
      const tocContainer = document.querySelector('.toc-sidebar-content');
      if (tocContainer) {
        tocContainer.removeEventListener('click', this.handleTocClick);
        tocContainer.addEventListener('click', this.handleTocClick);
      }
    },
    
    // 处理目录链接点击
    handleTocClick(event) {
      const link = event.target.closest('a[href^="#"]');
      if (link) {
        event.preventDefault();
        const targetId = link.getAttribute('href').substring(1);
        if (targetId) {
          this.scrollToHeading(targetId);
          this.showTocSidebar = false;
        }
      }
    }
  },
};
</script>

<style scoped>
/* 页面容器样式 */
.preview-page-container {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e7f1 100%);
  display: flex;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;
  position: relative;
}

/* 悬浮按钮容器样式 */
.floating-buttons-container {
  position: absolute;
  top: 5vh;
  left: 30px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  z-index: 100;
}

/* 通用悬浮按钮样式 */
.floating-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 2px solid rgba(255, 255, 255, 0.3);
  position: relative;
}

/* 返回按钮样式 */
.back-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.back-btn:hover {
  background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
  transform: scale(1.1) rotate(-15deg);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

/* 目录按钮样式 */
.toc-btn {
  background: linear-gradient(135deg, #9c27b0, #9575cd);
}

.toc-btn:hover {
  background: linear-gradient(135deg, #7b1fa2, #7e57c2);
  transform: scale(1.1) rotate(15deg);
  box-shadow: 0 6px 20px rgba(156, 39, 176, 0.4);
}

/* 主题按钮样式 */
.theme-btn {
  background: linear-gradient(135deg, #8e44ad, #9b59b6);
}

.theme-btn:hover {
  background: linear-gradient(135deg, #9b59b6, #8e44ad);
  transform: scale(1.1) rotate(15deg);
  box-shadow: 0 6px 20px rgba(142, 68, 173, 0.4);
}

/* 导出按钮样式 - 保持原始绿色色系 */
.export-btn {
  background: rgb(9, 136, 66);
}

.export-btn:hover {
  background: rgb(9, 136, 66);
  transform: scale(1.1) rotate(15deg);
  box-shadow: 0 6px 20px rgba(93, 219, 104, 0.644);
}

/* 主题选择下拉框样式 */
.theme-select-dropdown {
  position: absolute;
  width: 100%;
  height: 100%;
  border: none;
  background: transparent;
  outline: none;
  box-shadow: none;
  opacity: 0;
  z-index: -1;
}

.theme-select-dropdown >>> .el-input__inner {
  border: none !important;
  background: transparent !important;
  color: transparent !important;
  text-align: center;
  font-size: 12px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 5px;
  box-sizing: border-box;
}

.theme-select-dropdown >>> .el-input__suffix {
  display: none;
}

.theme-select-dropdown >>> .el-input__suffix-inner {
  display: none;
}

/* 图标样式 */
.floating-btn i {
  color: white;
  font-size: 18px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  z-index: 2;
  pointer-events: none;
}

.preview-wrapper {
  width: 70%; 
  height: 99%;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1); 
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  background: #fff;
}

.preview-wrapper.with-toc {
  width: 85%;
  margin-left: 15%;
}

/* 目录侧边栏样式 */
.toc-sidebar {
  position: absolute;
  top: 0;
  left: 0;
  height: 97.5vh;
  width: 15vw;
  background: linear-gradient(135deg, #f3e5f5, #ffffff);
  box-shadow: 4px 0 20px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  border-right: 1px solid rgba(156, 39, 176, 0.1);
  border-radius: 12px 0 0 12px;
}

.toc-sidebar-header {
  padding: 20px;
  border-bottom: 1px solid rgba(156, 39, 176, 0.2);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(156, 39, 176, 0.05);
  border-radius: 12px 0 0 0;
}

.toc-sidebar-header h3 {
  margin: 0;
  font-size: 18px;
  color: #7b1fa2;
  font-weight: 600;
}

.toc-close-btn {
  font-size: 20px;
  padding: 0;
  color: #9c27b0;
  transition: all 0.2s ease;
}

.toc-close-btn:hover {
  color: #7b1fa2;
  transform: rotate(90deg);
}

.toc-sidebar-content {
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 0 0 0 12px;
}

.toc-sidebar-content ul {
  list-style: none;
  padding-left: 0;
  margin: 0;
}

.toc-sidebar-content li {
  margin: 8px 0;
  position: relative;
}

.toc-sidebar-content a {
  text-decoration: none;
  color: #5e35b1;
  display: block;
  padding: 8px 12px;
  border-radius: 6px;
  transition: all 0.3s ease;
  font-size: 14px;
  border: 1px solid transparent;
}

.toc-sidebar-content a:hover {
  background: linear-gradient(135deg, rgba(156, 39, 176, 0.1), rgba(123, 31, 162, 0.1));
  color: #7b1fa2;
  border-color: rgba(156, 39, 176, 0.3);
  transform: translateX(4px);
}

.toc-sidebar-content ul ul { padding-left: 24px; }
.toc-sidebar-content ul ul ul { padding-left: 24px; }
.toc-sidebar-content ul ul a { color: #7e57c2; font-size: 13px; }
.toc-sidebar-content ul ul ul a { color: #9575cd; font-size: 12px; }

.preview-body {
  flex: 1;
  position: relative;
  /* overflow-y: auto; */
  overflow: hidden;
  padding: 20px;
}

.slide-enter-active, .slide-leave-active {
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.slide-enter, .slide-leave-to {
  transform: translateX(-100%);
}

.toc-sidebar-content::-webkit-scrollbar { width: 8px; }
.toc-sidebar-content::-webkit-scrollbar-track { background: #f3e5f5; border-radius: 4px; }
.toc-sidebar-content::-webkit-scrollbar-thumb { background: linear-gradient(135deg, #9c27b0, #7e57c2); border-radius: 4px; }
.toc-sidebar-content::-webkit-scrollbar-thumb:hover { background: linear-gradient(135deg, #7b1fa2, #9575cd); }

@media (max-width: 768px) {
  .preview-page-container { padding: 10px; }
  .preview-wrapper { width: 100%; height: 95%; }
  .preview-wrapper.with-toc { width: 85%; margin-left: 15%; }
  .toc-sidebar { height: 95%; }
  .floating-buttons-container { top: 20px; left: 20px; }
}

/* ==========================================================================
   打印 (PDF导出) 专用样式
   ========================================================================== */
@media print {
  @page {
    margin: 10mm;
    size: auto;
  }

  /* 隐藏 UI 控件 */
  .floating-buttons-container,
  .toc-sidebar,
  .floating-btn,
  .toc-close-btn {
    display: none !important;
  }

  /* 顶级容器重置 */
  html, body, #app, .preview-page-container {
    width: 100% !important;
    height: auto !important;
    background: white !important;
    margin: 0 !important;
    padding: 0 !important;
    overflow: visible !important;
    display: block !important;
  }

  /* 去除包裹层的圆角和阴影 */
  .preview-wrapper {
    width: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
    box-shadow: none !important;
    border: none !important;
    border-radius: 0 !important; 
    background: white !important;
    overflow: visible !important;
    display: block !important;
  }

  .preview-body {
    padding: 0 !important;
    margin: 0 !important;
    overflow: visible !important;
    /* overflow-y: auto !important; */
    border-radius: 0 !important;
  }

  /* 强制重置内容容器样式 */
  #preview-content, 
  .markdown-theme-base,
  [class*="theme-"] {
    border: none !important;
    border-radius: 0 !important; 
    box-shadow: none !important;
    padding: 0 !important;
    margin: 0 !important;
    background: transparent !important;
  }

  /* --- 核心修复：修复代码块打印无法换行和出现滚动条的问题 --- */
  pre, code {
    /* 强制换行逻辑 */
    white-space: pre-wrap !important;       /* 保留空白但允许自动换行 */
    word-wrap: break-word !important;        /* 允许在单词内换行 */
    overflow-wrap: break-word !important;
    word-break: break-all !important;        /* 强制在任何字符间断行，防止撑破页面 */
    
    /* 废弃滚动条 */
    overflow: visible !important;            /* 关键：防止渲染无效滚动条 */
    
    /* 布局微调 */
    max-width: 100% !important;
    display: block !important;
    border-radius: 0 !important;
  }

  /* 分页控制 */
  img {
    break-inside: avoid;
    max-width: 100% !important;
  }

  h1, h2, h3, h4, h5 {
    break-after: avoid;
    margin-top: 15pt !important;
  }
}
</style>